import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useState } from 'react';

// Constants for better maintainability
const TOKEN_ERRORS = [
  'Missing Refresh Token',
  'invalid_grant',
  'Unknown or invalid refresh token',
  'Login required'
];

export const useAuthHelper = () => {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  // Clear Auth0 cache
  const clearAuth0Cache = useCallback(() => {
    const auth0Pattern = /^@@auth0spajs@@/;
    
    Object.keys(localStorage)
      .filter(key => auth0Pattern.test(key))
      .forEach(key => localStorage.removeItem(key));
    
    Object.keys(sessionStorage)
      .filter(key => auth0Pattern.test(key))
      .forEach(key => sessionStorage.removeItem(key));
  }, []);

  // Clear cache and re-authenticate
  const clearCacheAndReauth = useCallback(async () => {
    clearAuth0Cache();
    await loginWithRedirect({
      authorizationParams: { 
        prompt: 'login'
      }
    });
  }, [clearAuth0Cache, loginWithRedirect]);

  const getSafeAccessToken = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }
    
    try {
      return await getAccessTokenSilently({ 
        cacheMode: 'cache-first'
      });
    } catch (error) {
      // Handle specific token errors
      const isTokenError = TOKEN_ERRORS.some(errorType => 
        error.message.includes(errorType)
      );
      
      if (isTokenError) {
        setIsTokenExpired(true);
        clearAuth0Cache();
        
        // Add a brief delay before redirect for better UX
        setTimeout(() => {
          loginWithRedirect({
            authorizationParams: { 
              prompt: 'login'
            }
          });
        }, 2000); // 2 seconds delay
        
        // Return null instead of throwing to prevent error flash
        return null;
      }
      
      throw error; // Re-throw other errors
    }
  }, [getAccessTokenSilently, loginWithRedirect, isAuthenticated, clearAuth0Cache]);

  return {
    getSafeAccessToken,
    clearAuth0Cache,
    clearCacheAndReauth,
    isTokenExpired
  };
};
